import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import Text from 'components/_UI/Text';
import Fieldset from 'components/_UI/Fieldset';
import Loader from 'components/_UI/Loader';
import { useForm } from 'react-hook-form';
import fieldValidations from 'helpers/fieldValidations';
import loginApi from 'api/requests/login';
import { AlertCircle, ArrowLeft, CheckCircle, Shield } from 'lucide-react';

export default function ForgotPasswordCode({ email, onNext, onBack }) {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(600);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  async function onSubmit({ code }) {
    setIsLoading(true);
    setErrorMessage('');

    try {
      await loginApi.verifyCode({ code });
      onNext(code);
    } catch (error) {
      if (error?.response?.status === 422) {
        setErrorMessage('Código inválido. Verifique e tente novamente.');
      } else {
        setErrorMessage('Ocorreu um erro. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Container>
      <HeaderArea>
        <BackButton onClick={onBack} aria-label="Voltar">
          <ArrowLeft size={18} color={colors.night} />
        </BackButton>
        <StepIndicator>
          <StepDot />
          <StepDot active />
          <StepDot />
        </StepIndicator>
      </HeaderArea>

      <IconCircle>
        <Shield size={22} color={colors.occasionalPurple} />
      </IconCircle>
      <Title>Verificar código</Title>
      <Subtitle>
        Enviamos um código de verificação para <EmailHighlight>{email}</EmailHighlight>
      </Subtitle>

      {errorMessage && (
        <ErrorBanner>
          <AlertCircle size={16} color={colors.danger} />
          <Text color={colors.danger} name="small" type="bold">
            {errorMessage}
          </Text>
        </ErrorBanner>
      )}

      <FormWrapper onSubmit={handleSubmit(onSubmit)}>
        <FieldGroup>
          <FieldLabel>
            Código de verificação<Required>*</Required>
          </FieldLabel>
          <Fieldset
            layout="squired"
            placeholder="Digite o código"
            name="code"
            register={register}
            validations={fieldValidations.code('código')}
          />
          {errors.code?.message && (
            <ErrorText>{errors.code.message}</ErrorText>
          )}
        </FieldGroup>

        {countdown > 0 && (
          <CountdownRow>
            <CountdownText>
              Código expira em <CountdownTimer>{formatTime(countdown)}</CountdownTimer>
            </CountdownText>
          </CountdownRow>
        )}

        {countdown <= 0 && (
          <ExpiredBanner>
            <AlertCircle size={16} color={colors.occasionalOrange} />
            <Text color={colors.occasionalOrange} name="small" type="bold">
              Código expirado. Volte e solicite um novo código.
            </Text>
          </ExpiredBanner>
        )}

        {isLoading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          <SubmitButton type="submit" disabled={countdown <= 0}>
            <span>Verificar</span>
            <CheckCircle size={18} />
          </SubmitButton>
        )}
      </FormWrapper>
    </Container>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(12px); }
  to { opacity: 1; transform: translateX(0); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeIn} 0.25s ease-out;
`;

const HeaderArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.neutralDark};
  }
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const StepDot = styled.div`
  width: ${({ active }) => (active ? '24px' : '8px')};
  height: 8px;
  border-radius: 4px;
  background: ${({ active }) => (active ? colors.occasionalPurple : colors.neutralDark)};
  transition: all 0.3s ease;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f3eaff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.nightDark};
  margin: 0;
  text-align: center;

  @media screen and (max-width: ${breakpoints.sm}) {
    font-size: 18px;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${colors.night};
  margin-top: 4px;
  margin-bottom: 20px;
  text-align: center;
  line-height: 1.5;
`;

const EmailHighlight = styled.span`
  font-weight: 700;
  color: ${colors.nightDark};
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const FieldGroup = styled.div`
  width: 100%;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: ${colors.nightDark};
  letter-spacing: 0.01em;
  margin-bottom: 6px;
`;

const Required = styled.span`
  color: ${colors.danger};
`;

const ErrorText = styled.p`
  color: ${colors.danger};
  font-size: 12px;
  margin-top: 4px;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: #fef2f2;
  border: 1px solid ${colors.dangerLight};
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const ExpiredBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: #fff8f0;
  border: 1px solid ${colors.occasionalOrange}44;
  border-radius: 10px;
  padding: 12px 16px;
`;

const CountdownRow = styled.div`
  text-align: center;
`;

const CountdownText = styled.p`
  font-size: 13px;
  color: ${colors.night};
`;

const CountdownTimer = styled.span`
  font-weight: 700;
  color: ${colors.occasionalPurple};
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 280px;
  margin: 4px auto 0;
  padding: 12px 28px;
  background: ${colors.secondaryPurple};
  color: ${colors.neutralLight};
  border: 1px solid ${colors.secondaryPurple};
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 3px ${colors.nightLight};

  &:hover {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(128, 37, 232, 0.25);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 0;
`;
